import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title: string;
  description: string;
  image?: string;
  type?: 'website' | 'article' | 'product';
  canonicalUrl?: string;
  noIndex?: boolean;
  structuredData?: object;
}

const DEFAULT_IMAGE = 'https://zentrix-insights.lovable.app/og-image.png';
const SITE_NAME = 'Zentrix Insights';
const TWITTER_HANDLE = '@zentrix_insights';

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  image = DEFAULT_IMAGE,
  type = 'website',
  canonicalUrl,
  noIndex = false,
  structuredData,
}) => {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// Pre-configured SEO for common pages
export const HomePageSEO = () => (
  <SEOHead
    title="Zentrix Insights - Behavioral & Cognitive Assessment Platform"
    description="Understand your team with data-driven behavioral and cognitive assessments. Build better companies with personality insights, team analytics, and AI-powered recommendations."
    canonicalUrl="https://zentrixinsights.com"
    structuredData={{
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Zentrix Insights",
      "url": "https://zentrixinsights.com",
      "description": "Behavioral and cognitive assessment platform for talent evaluation and team building",
      "sameAs": [
        "https://twitter.com/zentrix_insights"
      ]
    }}
  />
);

export const BusinessPageSEO = () => (
  <SEOHead
    title="For Teams - Build Better Companies"
    description="Empower your organization with data-driven behavioral insights. Team analytics, role matching, and AI-powered recommendations to build high-performing teams."
    canonicalUrl="https://zentrixinsights.com/business"
  />
);

export const PricingPageSEO = () => (
  <SEOHead
    title="Pricing - 7-Day Free Trial"
    description="Simple pricing with a 7-day free trial. No credit card required. Unlimited assessments, AI insights, team analytics, and more."
    canonicalUrl="https://zentrixinsights.com/pricing"
  />
);

export const ProfilesPageSEO = () => (
  <SEOHead
    title="19 Behavioral Profiles - Personality Archetypes"
    description="Discover the 19 behavioral profiles in the ABCDE personality model. From The Luminary to The Harmonizer, understand what drives different personality types."
    canonicalUrl="https://zentrixinsights.com/profiles"
  />
);

export const TrainingPageSEO = () => (
  <SEOHead
    title="Training Resources - Learn Behavioral Assessment"
    description="Free training resources to help you interpret behavioral assessments, understand personality profiles, and apply insights to team building."
    canonicalUrl="https://zentrixinsights.com/training-public"
  />
);

export default SEOHead;
